/**
 * Recursive-descent parser for the RECODE arithmetic mini-language.
 *
 * Grammar (standard precedence, left-associative):
 *   expression := term     (('+' | '-') term)*
 *   term       := factor   (('*' | '/') factor)*
 *   factor     := number | '<<' ref '>>' | '(' expression ')'
 *
 * Used to validate what the user types into the arithmetic editor; errors are
 * surfaced inline (and disable Apply). On success it also yields an AST that
 * `generateArith` can re-emit canonically (used by tests).
 */
import { ArithNode, ArithOp } from "./arithGrammar"

export interface ArithParseResult {
  ast: ArithNode | null
  errors: string[]
}

interface Token {
  type: "num" | "ref" | "op" | "lparen" | "rparen"
  value: string
}

function tokenize(input: string, errors: string[]): Token[] {
  const tokens: Token[] = []
  let i = 0
  while (i < input.length) {
    const c = input[i]
    if (/\s/.test(c)) {
      i++
      continue
    }
    if (c === "<" && input[i + 1] === "<") {
      const end = input.indexOf(">>", i + 2)
      if (end === -1) {
        errors.push("Unterminated << … >> reference")
        break
      }
      const label = input.slice(i + 2, end).trim()
      if (!label) errors.push("Empty << >> reference")
      tokens.push({ type: "ref", value: label })
      i = end + 2
      continue
    }
    if (/[0-9]/.test(c)) {
      let j = i + 1
      while (j < input.length && /[0-9.]/.test(input[j])) j++
      const num = input.slice(i, j)
      if (!/^\d+(\.\d+)?$/.test(num)) errors.push(`Invalid number "${num}"`)
      tokens.push({ type: "num", value: num })
      i = j
      continue
    }
    if (c === "+" || c === "-" || c === "*" || c === "/") {
      tokens.push({ type: "op", value: c })
      i++
      continue
    }
    if (c === "(") {
      tokens.push({ type: "lparen", value: c })
      i++
      continue
    }
    if (c === ")") {
      tokens.push({ type: "rparen", value: c })
      i++
      continue
    }
    errors.push(`Unsupported character "${c}"`)
    i++
  }
  return tokens
}

class ArithReader {
  pos = 0
  constructor(
    private tokens: Token[],
    private errors: string[],
  ) {}

  private peek(): Token | undefined {
    return this.tokens[this.pos]
  }
  private next(): Token | undefined {
    return this.tokens[this.pos++]
  }

  parseExpression(): ArithNode | null {
    let left = this.parseTerm()
    if (!left) return null
    while (
      this.peek()?.type === "op" &&
      (this.peek()!.value === "+" || this.peek()!.value === "-")
    ) {
      const op = this.next()!.value as ArithOp
      const right = this.parseTerm()
      if (!right) return null
      left = { kind: "binary", op, left, right }
    }
    return left
  }

  private parseTerm(): ArithNode | null {
    let left = this.parseFactor()
    if (!left) return null
    while (
      this.peek()?.type === "op" &&
      (this.peek()!.value === "*" || this.peek()!.value === "/")
    ) {
      const op = this.next()!.value as ArithOp
      const right = this.parseFactor()
      if (!right) return null
      left = { kind: "binary", op, left, right }
    }
    return left
  }

  private parseFactor(): ArithNode | null {
    const t = this.peek()
    if (!t) {
      this.errors.push("Unexpected end of expression")
      return null
    }
    if (t.type === "num") {
      this.next()
      return { kind: "num", raw: t.value }
    }
    if (t.type === "ref") {
      this.next()
      return { kind: "ref", label: t.value }
    }
    if (t.type === "lparen") {
      this.next()
      const inner = this.parseExpression()
      if (!inner) return null
      if (this.peek()?.type === "rparen") {
        this.next()
        return inner
      }
      this.errors.push("Missing closing parenthesis")
      return null
    }
    this.errors.push(`Unexpected "${t.value}"`)
    return null
  }

  hasTrailing(): boolean {
    return this.pos < this.tokens.length
  }

  trailingValue(): string {
    return this.tokens[this.pos]?.value ?? ""
  }
}

export function parseArith(input: string): ArithParseResult {
  const errors: string[] = []
  const trimmed = input.trim()
  if (!trimmed) return { ast: null, errors: ["Expression is empty"] }

  const tokens = tokenize(trimmed, errors)
  if (tokens.length === 0) {
    return { ast: null, errors: errors.length ? errors : ["Expression is empty"] }
  }

  const reader = new ArithReader(tokens, errors)
  const ast = reader.parseExpression()
  if (ast && reader.hasTrailing()) {
    errors.push(`Unexpected trailing input near "${reader.trailingValue()}"`)
  }
  return { ast, errors }
}
